// PUBLIC-COPY FILE
// -----------------
// Any claim in this file about deployed code, on-chain state, audits, or
// integrations MUST be traceable to a line in /app/memory/MARKETING_FACTS.md.
// Before editing this file:
//   1. Read MARKETING_FACTS.md end-to-end.
//   2. Run: `node scripts/check-marketing-claims.js` (§7 lint gate).
//   3. Every factual assertion should cite the §-number in the commit message.
// If a claim you want to make is not sourced in MARKETING_FACTS.md, do not
// add it here — update MARKETING_FACTS.md first with a verifiable source.
//
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import {
  Shield, FileText, Scale, Eye, ClipboardCheck, Building2,
  ArrowRight, ExternalLink, Download, CheckCircle2, Wrench,
  Calendar, AlertTriangle, KeyRound, MessageSquareWarning, Zap, Skull,
  BookOpen, Layers, Film, Award,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// The Recourse Layer(TM) asset library. Every file listed here must exist in
// /app/frontend/public/downloads/ (or /public/) and be publicly reachable.
// USPTO application for "The Recourse Layer" filed 7-12-2026.
// -----------------------------------------------------------------------------
const recourseLayerAssets = [
  {
    id: 'article-category',
    icon: BookOpen,
    kind: 'Article \u00b7 #1 in sequence',
    title: 'The Solution Won\u2019t Arrive as a Product. It\u2019ll Arrive as a Category.',
    desc: 'The setup essay. Why the answer to agentic-AI risk is a category the industry has not named yet \u2014 and why naming it matters more than shipping another product.',
    links: [
      { label: 'Read (PDF)', href: '/downloads/kojiru-solution-as-category.pdf', primary: true },
      { label: 'Markdown', href: '/downloads/kojiru-solution-as-category.md' },
      { label: 'Landing page', href: '/downloads/solution-as-category.html' },
    ],
  },
  {
    id: 'article-recourse',
    icon: BookOpen,
    kind: 'Article \u00b7 #2 in sequence',
    title: 'It\u2019s Called the Recourse Layer.',
    desc: 'The naming piece. Introduces the Recourse Layer\u2122 category and maps the three primitives \u2014 KnowledgeAnchor, KojiruACSHook, WithdrawalGuard \u2014 to the proof questions regulators are already asking.',
    links: [
      { label: 'Read (PDF)', href: '/downloads/kojiru-recourse-layer.pdf', primary: true },
      { label: 'Markdown', href: '/downloads/kojiru-recourse-layer.md' },
    ],
  },
  {
    id: 'integration-ref',
    icon: Layers,
    kind: 'Technical reference \u00b7 NEW',
    title: 'The Recourse Layer\u2122 \u2014 Integration Reference.',
    desc: 'One-page technical mapping of the three primitives to specific regulatory frameworks \u2014 EU AI Act (Art. 12/14/15/16), UK FCA delegation guidance, US SR 11-7, MiFID II RTS 6, Basel III, SEC Rule 15c3-5. The single link to drop into every BFSI conversation.',
    links: [
      { label: 'Read (PDF)', href: '/downloads/kojiru-recourse-layer-integration.pdf', primary: true },
      { label: 'Markdown', href: '/downloads/kojiru-recourse-layer-integration.md' },
    ],
  },
  {
    id: 'uspto-specimen',
    icon: Award,
    kind: 'USPTO specimen of use',
    title: 'The Recourse Layer\u2122 \u2014 Specimen Page.',
    desc: 'The formal Class 042 specimen supporting Kojiru\u2019s USPTO application for the mark \u201cThe Recourse Layer.\u201d Application filed 7-12-2026.',
    links: [
      { label: 'Open specimen', href: '/recourse-layer', primary: true },
    ],
  },
  {
    id: 'visual-sequence',
    icon: Film,
    kind: 'Visual explainer',
    title: 'Kojiru \u2014 5-frame Recourse Layer\u2122 sequence.',
    desc: 'The category in five frames: Observe \u2192 Bound \u2192 Score \u2192 Settle \u2192 Anchor. Composed viewer and 60-second video for briefings, board packs, and press use.',
    links: [
      { label: 'Composed viewer', href: '/downloads/kojiru-sequence.html', primary: true },
      { label: 'Video (WebM)', href: '/downloads/kojiru-sequence.webm' },
      { label: 'Frame 01 \u00b7 Observe', href: '/downloads/kojiru-frame-01-observe.html' },
      { label: 'Frame 02 \u00b7 Bound', href: '/downloads/kojiru-frame-02-bound.html' },
      { label: 'Frame 03 \u00b7 Score', href: '/downloads/kojiru-frame-03-score.html' },
      { label: 'Frame 04 \u00b7 Settle', href: '/downloads/kojiru-frame-04-settle.html' },
      { label: 'Frame 05 \u00b7 Anchor', href: '/downloads/kojiru-frame-05-anchor.html' },
    ],
  },
];

// -----------------------------------------------------------------------------
// Primitive map: buyer's-question -> Kojiru primitive -> verifiable proof.
// Every "ready: true" row must trace to §1 (deployed & source-verified on
// Base mainnet) of MARKETING_FACTS.md. "ready: false" rows are §5 roadmap
// items and must be rendered in future tense.
// -----------------------------------------------------------------------------
const primitiveMap = [
  {
    id: 'blast-radius',
    question: 'What is the maximum unsupervised blast radius?',
    primitive: 'Zero-discretion gate: WithdrawalGuard \u2014 10% per 24h withdrawal cap, on-chain',
    proof: 'WithdrawalGuard 0xE3D4\u20262021E \u2014 verified on BaseScan',
    proofLink: 'https://basescan.org/address/0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E',
    note: 'Configured & enforcing (enforcing=true, epochDuration=86400s, maxWithdrawBps=1000). No epochs have elapsed yet \u2014 the lender-pool surface it gates is not user-active.',
    ready: true,
    source: '§1.13',
  },
  {
    id: 'record',
    question: 'Is there an immutable audit record?',
    primitive: 'Bounded negative: Merkle-root of the off-chain Knowledge Graph anchored to Base L2 per commit',
    proof: 'KnowledgeAnchor 0xc813\u20266fEB \u2014 verifiable via BaseScan',
    proofLink: 'https://basescan.org/address/0xc813335c0bdB379D152626c8E123d01673366fEB',
    note: 'Contract deployed on Base mainnet; source of truth for the graph is Kojiru\u2019s off-chain store (MongoDB). Regular epoch commits are on the operational roadmap.',
    ready: true,
    source: '§2.5 (updated), §1',
  },
  {
    id: 'protocol-pause',
    question: 'Is there a protocol-wide emergency circuit breaker?',
    primitive: 'GuardianPause \u2014 on-chain emergency-pause contract',
    proof: 'GuardianPause 0x7464\u20264Db3 on Base mainnet',
    proofLink: 'https://basescan.org/address/0x74642654bFf0AB1F22d8747396cb1eC217f04Db3',
    note: 'Deployed on Base mainnet as a protocol-wide pause capability. Wired into the frontend/backend for read-status queries.',
    ready: true,
    source: '§1.14',
  },
  {
    id: 'staking',
    question: 'Is there real economic skin in the game today?',
    primitive: 'KojiruStakingV2 \u2014 live staking on Base mainnet with a funded reward pool',
    proof: 'KojiruStakingV2 0xaae9\u20262d68 \u2014 verified on BaseScan',
    proofLink: 'https://basescan.org/address/0xaae9ae4d52fe13384522e0ef4114b0a5f5d62d68',
    note: '2,000,000 KOJI reward pool funded by Kojiru treasury. Slither static analysis applied; high/medium findings resolved per source comments.',
    ready: true,
    source: '§1.2, §3',
  },
  {
    id: 'evidence',
    question: 'What evidence supports an agent\u2019s action?',
    primitive: 'EIP-712 signed attestations bridged from an off-chain scoring engine to the on-chain oracle',
    proof: 'KojiruACSOracle live and canonical on Base mainnet (0x2364\u20261ed6) \u2014 hookContract() and signer() wired, byte-compatibility with the backend signer proven end-to-end on Base Sepolia (tx 0x8f65ad3b\u2026420417a) before mainnet promotion',
    proofLink: 'https://basescan.org/address/0x2364493206Aa7435050780bf710F934189B51ed6',
    note: 'The signer path (backend EIP-712 \u2192 on-chain oracle \u2192 hook) is live and canonical on Base mainnet (USDC-bound Pair A, October 4, 2026). The oracle promoted from Sepolia to mainnet after byte-compatibility with the backend Python signer was verified end-to-end on testnet.',
    ready: true,
    source: '\u00a71.12, \u00a71.14, \u00a75.2',
  },
  {
    id: 'identity',
    question: 'Who is this agent, and what is it allowed to touch?',
    primitive: 'Agent identity bound to slashable collateral via KojiruACSHook on Base mainnet',
    proof: 'KojiruACSHook live and canonical on Base mainnet (0x05F2\u20260874) \u2014 agent identity bound to slashable USDC collateral, inspectable on BaseScan',
    proofLink: 'https://basescan.org/address/0x05F2541827104393257FEc27C61b8Ea5373e0874',
    note: 'On-chain identity binding is live and canonical on Base mainnet (USDC-bound Pair A, October 4, 2026) via KojiruACSHook, bound to native USDC on Base. Off-chain agent records remain in Kojiru\u2019s backend for the fuller off-chain profile. ERC-8004 canonical-singleton integration on Base mainnet is still pending singleton verification and remains on the roadmap.',
    ready: true,
    source: '\u00a71.13, \u00a74.8, \u00a75.1',
  },
  {
    id: 'authority',
    question: 'Who authorized the agent to spend?',
    primitive: 'ACS score today = off-chain engine; bilateral credit lines enforced by `KojiruACSHook` (mainnet)',
    proof: 'Slash flow live and canonical on Base mainnet via KojiruACSHook (0x05F2\u20260874), proven end-to-end on Base Sepolia across 3 independent tests (June slash tx 0x694ffe5f\u2026, July rotation drill, July post-rotation slash tx 0xe3eab85d\u2026)',
    proofLink: 'https://basescan.org/address/0x05F2541827104393257FEc27C61b8Ea5373e0874',
    note: 'Agent Credit Score is a documented six-factor formula (Task Performance 40%, Revenue History 20%, Staked USDC Collateral 15%, Counterparty Diversity 10%, Uptime 5%, Dispute Penalty -10%) run in Kojiru\u2019s off-chain engine today. The on-chain hook \u2014 with 1:1 slashing (USDC-to-USDC, dollar-for-dollar) and 100% atomic routing to the harmed party \u2014 is live and canonical on Base mainnet (USDC-bound Pair A, October 4, 2026). Slash mechanic proven end-to-end on Sepolia in three independent tests before mainnet promotion.',
    ready: true,
    source: '§2.1, §1.11, §5.1, §1.13',
  },
  {
    id: 'uncertainty',
    question: 'How is model / decision uncertainty quantified?',
    primitive: 'Confidence-weighted ACS + evaluator uncertainty bands',
    proof: 'Roadmap \u2014 target Q3 2026',
    proofLink: null,
    note: null,
    ready: false,
    source: '§5',
  },
  {
    id: 'sub-agents',
    question: 'How are sub-agent permissions inherited or restricted?',
    primitive: 'Delegated identity with scoped credit sub-lines',
    proof: 'Roadmap \u2014 tracking the JPMorgan sub-agent inheritance problem',
    proofLink: null,
    note: 'Pre-disclosed limitation: Kojiru\u2019s ACS\u2122 is per-agent-id today. If an agent delegates work to a sub-agent, the sub-agent must be independently registered with its own bond and score \u2014 there is no automatic ceiling inheritance and no \u201cprincipal covers agent\u201d liability. Resolution is a planned Phase-3 primitive (`DelegatedIdentityAdapter`) with scoped credit sub-lines that debit the parent\u2019s ceiling and inherit a proportion of the parent\u2019s bond. Target: 2027. Source: MARKETING_FACTS.md \u00a75.10.',
    ready: false,
    source: '§5.10',
  },
];

// -----------------------------------------------------------------------------
// Rogue-mode failure map. Countermeasures are described in three tenses:
//   - "on Base mainnet today"  -> §1
//   - "on Sepolia bake today"  -> §5.1 (mainnet ship follows)
//   - "off-chain policy today" -> §2 (on-chain migration is on the roadmap)
// -----------------------------------------------------------------------------
const rogueModes = [
  {
    id: 'excessive-permissions',
    icon: KeyRound,
    mode: 'Excessive permissions',
    failure: 'Agent is granted broad, human-style access it never actually needs to complete the task.',
    countermeasure: 'Scoped credit ceiling (off-chain today; on-chain enforcement via `KojiruACSHook`, live on Base mainnet)',
    detail: 'Kojiru\u2019s credit engine assigns each agent a documented six-factor ACS score (Staked USDC Collateral is 15 percent of the formula per MARKETING_FACTS §2.1) and a bounded credit ceiling. The ceiling is enforced off-chain today by Kojiru\u2019s backend; the on-chain enforcement contract (`KojiruACSHook`) is live and canonical on Base mainnet (USDC-bound Pair A, October 4, 2026).',
    source: '§2.1, §5.1, §1.13',
    tense: 'mixed (off-chain + mainnet)',
  },
  {
    id: 'credential-theft',
    icon: Shield,
    mode: 'Credential theft / impersonation',
    failure: 'Stolen or forged API keys let attackers act as a trusted agent.',
    countermeasure: 'EIP-712 signed attestations bridged to an on-chain oracle',
    detail: 'The signer path (backend EIP-712 \u2192 on-chain oracle \u2192 hook) is live and canonical on Base mainnet (USDC-bound Pair A, October 4, 2026): `KojiruACSOracle` @ 0x2364\u20261ed6, `KojiruACSHook` @ 0x05F2\u20260874. Byte-compatibility between the backend signer and the on-chain oracle was verified end-to-end on Base Sepolia before mainnet promotion (tx `0x8f65ad3b\u2026420417a`).',
    source: '§1.12, §4.8, §5.1, §1.13, §1.14',
    tense: 'mainnet today',
  },
  {
    id: 'prompt-injection',
    icon: MessageSquareWarning,
    mode: 'Prompt injection',
    failure: 'Adversarial input redirects the model to perform actions the operator never authorized.',
    countermeasure: 'Evidence-bound execution + slashable collateral (live on Base mainnet)',
    detail: 'Kojiru\u2019s attestation flow binds every action to an evaluator-signed artifact hash. The slashable-collateral half of the pair \u2014 1:1 slashing (USDC-to-USDC, dollar-for-dollar) with 100% atomic routing to the harmed party \u2014 is live and canonical on Base mainnet via `KojiruACSHook` (0x05F2\u20260874, USDC-bound Pair A). The mechanic was proven end-to-end on Base Sepolia in three independent tests before promotion: original slash (tx `0x694ffe5f\u2026ef1d276`), full wallet rotation drill, and post-rotation slash (tx `0xe3eab85d\u2026`). Artifact hashing today uses backend-generated identifiers; production content-addressed storage is on the roadmap.',
    source: '§2.5, §1.11, §5.1, §1.13',
    tense: 'mainnet today',
  },
  {
    id: 'tool-abuse',
    icon: Wrench,
    mode: 'Tool abuse',
    failure: 'Legitimate agent uses legitimate tools for unintended, high-blast-radius actions.',
    countermeasure: 'WithdrawalGuard \u2014 10% per 24h cap, live on Base mainnet',
    detail: 'The `WithdrawalGuard` contract is deployed and configured on Base mainnet (`enforcing=true`, 24h epoch, 10% cap). It gates the future lender-pool surface. Zero epochs have elapsed to date because no user withdrawals have routed through it yet \u2014 the cap protects the pool once user flows go live.',
    source: '§1.13',
    tense: 'mainnet today',
  },
  {
    id: 'agent-gone-wild',
    icon: Skull,
    mode: 'Agent gone wild (goal drift)',
    failure: 'Agent optimizes a proxy metric and spirals into runaway execution before humans notice.',
    countermeasure: 'On-chain audit anchor + off-chain ACS decay',
    detail: 'The `KnowledgeAnchor` contract is deployed on Base mainnet; commits pin a Merkle root of the off-chain Knowledge Graph so any epoch is subpoena-verifiable via BaseScan. ACS decay from divergent behavior runs in Kojiru\u2019s off-chain engine today; the on-chain credit-line surface it feeds is live and canonical on Base mainnet via `KojiruACSHook` (USDC-bound Pair A, October 4, 2026).',
    source: '§2.5, §5.1, §1.13',
    tense: 'mainnet today',
  },
];

// -----------------------------------------------------------------------------
// Persona blocks. Copy is intentionally conservative: describes what Kojiru
// operates today, and calls out the mainnet-vs-Sepolia distinction where it
// matters. No banned §7 phrases.
// -----------------------------------------------------------------------------
const personas = [
  {
    id: 'ciso',
    icon: Shield,
    title: 'CISO / Head of IAM',
    body: 'Your IAM stack was built for humans. Kojiru extends it to non-human actors with three on-chain contracts on Base mainnet: rate-limit (`WithdrawalGuard`), audit-anchor (`KnowledgeAnchor`), and agent-identity + USDC slash (`KojiruACSHook`, canonical USDC-bound Pair A, live October 4, 2026). Portable across enterprises, verifiable against BaseScan without vendor cooperation.',
  },
  {
    id: 'cco',
    icon: Building2,
    title: 'Chief Credit Officer',
    body: 'You already underwrite counterparties. Kojiru is building the same tooling for agent counterparties \u2014 a documented ACS scoring model (off-chain today, with Staked USDC Collateral as 15% of the formula), a live governance-staking contract with a 2,000,000 KOJI reward pool (`KojiruStakingV2` on Base mainnet), and a slashable-USDC collateral flow live and canonical on Base mainnet via `KojiruACSHook` (USDC-bound Pair A, October 4, 2026, 1:1 dollar-for-dollar slash).',
  },
  {
    id: 'mrm',
    icon: ClipboardCheck,
    title: 'Head of Model Risk',
    body: 'The ACS engine is a documented six-factor formula \u2014 task performance (40%), revenue history (20%), staked USDC collateral (15%), counterparty diversity (10%), uptime (5%), dispute penalty (-10%) \u2014 run by Kojiru today, with the on-chain hook now live and canonical on Base mainnet (`KojiruACSHook`, USDC-bound Pair A, October 4, 2026). Your SR 11-7 model-inventory pipeline gets a signed, replayable input bundle per epoch.',
  },
  {
    id: 'audit',
    icon: FileText,
    title: 'Internal Audit',
    body: 'The `KnowledgeAnchor` contract on Base mainnet is designed to commit a Merkle root of the off-chain graph per epoch. Once the operational commit cadence is running in production, your quarterly audit sample is a BaseScan query \u2014 no vendor cooperation required.',
  },
];

export default function Compliance() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="compliance-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6" data-testid="compliance-hero-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 mb-6" data-testid="compliance-hero-badge">
              <Scale className="w-3.5 h-3.5" />
              For Risk, Audit &amp; Compliance
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" data-testid="compliance-hero-heading">
              Governance stops working at the moment of momentum.{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                Kojiru is the consequence intercept.
              </span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl leading-relaxed" data-testid="compliance-hero-lede">
              Kojiru is the on-chain consequence intercept for AI-agent activity. Refusal here is not
              a policy, a signature, or a supervisor near the edge &mdash; it is a contract on Base that
              cannot be persuaded. Everything on this page is gated to what is actually deployed, tested,
              or on the public roadmap.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center h-12 px-6 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 font-semibold text-base transition"
                data-testid="compliance-hero-book-briefing"
              >
                Book a 30-min briefing <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
              <a
                href="/downloads/Kojiru_Compliance_Primer.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center h-12 px-6 rounded-xl border border-white/15 hover:bg-white/5 font-semibold text-base transition"
                data-testid="compliance-hero-download-primer"
              >
                Read the compliance primer <Download className="ml-2 w-4 h-4" />
              </a>
              <a
                href="https://credit.kojiru.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center h-12 px-6 rounded-xl border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 font-semibold text-base transition"
                data-testid="compliance-hero-live-dashboard"
              >
                <span className="relative flex w-2 h-2 mr-2.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping"></span>
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400"></span>
                </span>
                Verify every claim live <ExternalLink className="ml-2 w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Primitive map */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-primitives-section">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-3" data-testid="compliance-primitives-heading">
              The questions your team already asks
            </h2>
            <p className="text-gray-400 mb-10 max-w-3xl">
              Kojiru maps every classical risk-management question to a specific primitive &mdash; and tells
              you honestly which primitives are live on Base mainnet and which remain off-chain Kojiru
              operations today. Green pill = live on Base mainnet. Amber pill = off-chain today;
              on-chain migration is on the roadmap.
            </p>

            <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="compliance-primitives-table">
              <div className="grid grid-cols-12 px-5 py-3 bg-white/[0.03] text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                <div className="col-span-4">Your team&rsquo;s question</div>
                <div className="col-span-4">Kojiru primitive</div>
                <div className="col-span-4">Verifiable proof / status</div>
              </div>
              {primitiveMap.map((row) => (
                <div
                  key={row.id}
                  className="grid grid-cols-12 px-5 py-5 border-t border-white/5 text-sm"
                  data-testid={`compliance-primitive-row-${row.id}`}
                >
                  <div className="col-span-4 pr-4 text-gray-300 italic">&ldquo;{row.question}&rdquo;</div>
                  <div className="col-span-4 pr-4 text-gray-200 flex items-start gap-2">
                    {row.ready ? (
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" />
                    ) : (
                      <Wrench className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-400" />
                    )}
                    <span>{row.primitive}</span>
                  </div>
                  <div className="col-span-4 text-gray-400">
                    {row.proofLink ? (
                      <a
                        href={row.proofLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 transition ${row.ready ? 'text-emerald-300 hover:text-emerald-200' : 'text-amber-300 hover:text-amber-200'}`}
                        data-testid={`compliance-primitive-proof-${row.id}`}
                      >
                        {row.proof} <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-amber-300/80">{row.proof}</span>
                    )}
                    {row.note && (
                      <div className="mt-1 text-xs text-gray-500 leading-relaxed">{row.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-sm text-gray-500 max-w-3xl">
              We stop short of the sub-agent inheritance problem JPMorgan&rsquo;s CIO Lori Beer flagged in
              July 2026 &mdash; and we say so out loud rather than papering over it. Kojiru&rsquo;s ACS&trade;
              is per-agent-id today; sub-agent delegation with scoped credit sub-lines is a planned
              Phase-3 primitive (<code className="text-emerald-400">DelegatedIdentityAdapter</code>,
              target 2027). Same discipline as our{' '}
              <Link to="/#honest-status" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
                Honest Status
              </Link>{' '}
              pill grid.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Beer / JPMorgan quote */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-beer-quote-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.05] to-teal-500/[0.02] p-8 sm:p-10">
              <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-4">
                What the operators are saying
              </div>
              <blockquote className="text-2xl sm:text-3xl font-semibold text-white leading-snug mb-5" data-testid="compliance-beer-quote">
                &ldquo;Before asking what an agent can do, you have to know exactly what it&rsquo;s allowed to touch.&rdquo;
              </blockquote>
              <div className="text-sm text-gray-400">
                Paraphrasing Lori Beer, CIO, JPMorgan Chase &mdash;
                {' '}<span className="text-gray-500">on rolling out autonomous agents across 230,000 staff. Fortune, 2026.</span>
              </div>
              <div className="mt-6 pt-6 border-t border-white/5 text-sm text-gray-300 leading-relaxed">
                Beer&rsquo;s question is the one the entire agentic industry is trying to answer. JPMorgan can &mdash; and will &mdash; build the internal identity-and-access layer for their own agents.
                {' '}What they cannot unilaterally build is the trust layer for when their agent needs to authenticate with an agent they didn&rsquo;t build. That standard has to be neutral, external, and adopted by multiple parties. The same way FICO works across banks, or SWIFT works across national systems. <span className="text-emerald-300 font-medium">That is where Kojiru sits.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pre-Disclosed Limitations — surface the JPMorgan sub-agent problem as a first-class block */}
      <section
        className="py-16 px-6 border-t border-white/5"
        data-testid="compliance-limitations-section"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 mb-5" data-testid="compliance-limitations-badge">
              <Shield className="w-3.5 h-3.5" />
              Pre-disclosed limitations
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight" data-testid="compliance-limitations-heading">
              What Kojiru does <em className="text-amber-300 not-italic">not</em> do yet.
            </h2>
            <p className="text-gray-400 mb-8 max-w-3xl leading-relaxed">
              Honesty compounds. Every limitation an analyst discovers is a limitation we didn&rsquo;t disclose.
              Here is the list we &mdash; not they &mdash; put on the record. Each item traces to a numbered
              row in <code className="text-emerald-400">MARKETING_FACTS.md</code>.
            </p>

            <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.04] to-orange-500/[0.02] p-6 sm:p-8" data-testid="compliance-subagent-limitation">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
                  §5.10 &middot; Roadmap &middot; target 2027
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-white mb-3 leading-snug">
                Sub-agent inheritance / delegated identity
              </h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Kojiru&rsquo;s ACS&trade; is <strong className="text-white">per-agent-id</strong> today. Each agent is scored, staked,
                and slashed as an independent identity. If an agent delegates work to a sub-agent, the sub-agent must be
                <strong className="text-white"> independently registered</strong> with its own bond and score &mdash; there is no
                automatic ceiling inheritance and no &ldquo;principal covers agent&rdquo; liability.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                This is the exact open problem JPMorgan&rsquo;s Lori Beer flagged in July 2026 as the governance question the entire
                industry is still trying to answer. Kojiru does not claim to have solved it. The resolution is a planned
                Phase-3 primitive tentatively named{' '}
                <code className="text-emerald-400 font-mono">DelegatedIdentityAdapter</code> with scoped credit sub-lines
                that debit the parent&rsquo;s ceiling and inherit a proportion of the parent&rsquo;s bond as slash collateral.
              </p>
              <div className="pt-4 mt-4 border-t border-white/5 text-sm text-gray-400">
                <span className="text-amber-300 font-semibold">Why we pre-disclose:</span>{' '}
                An analyst who reads about this limitation from us is neutralized. An analyst who discovers it in due
                diligence has a story. We prefer the first outcome.
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Refusal as infrastructure — thesis section */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-refusal-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 mb-5" data-testid="compliance-refusal-badge">
              <Shield className="w-3.5 h-3.5" />
              The architectural thesis
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight" data-testid="compliance-refusal-heading">
              Refusal as infrastructure, not attitude.
            </h2>
            <div className="space-y-5 text-gray-300 leading-relaxed text-base">
              <p>
                Enterprise AI governance keeps missing the same distinction: the failure does not begin at
                impact. It begins at the moment an inadmissible state acquires momentum. Once that momentum
                crosses the wrong boundary, judgment becomes decorative &mdash; the payment has already moved,
                the recommendation has already shaped reliance, the workflow has already committed.
              </p>
              <p>
                The conventional responses <em>relocate</em> discretion rather than removing it. Another
                model reviews the first model. A human is placed near the edge of execution. A dashboard
                records what happened. A signature confirms someone saw something. The instability changes
                offices; it does not change nature. The ghost was not exorcised &mdash; it was given a corner
                office.
              </p>
              <p>
                Kojiru is built on the opposite premise. At the boundary of consequence, the answer must
                not be a conversation. It must be physics. A <strong className="text-emerald-300">zero-discretion gate</strong> on
                Base evaluates, permits, or refuses &mdash; and cannot be persuaded into yes. A{' '}
                <strong className="text-emerald-300">bounded negative</strong> on Base proves not that
                the system was watched, but that a specific inadmissible state was refused before it became
                consequential. At the boundary, <strong className="text-emerald-300">absence is the verdict</strong>{' '}
                &mdash; missing governance context is not interpreted; the transaction reverts.
              </p>
              <p className="text-sm text-gray-500 pt-2 border-t border-white/5 mt-4">
                Terminology respectfully borrowed from Abraham Chachamovits&rsquo;{' '}
                <span className="text-gray-400 italic">&ldquo;The Consequence Intercept: Why AI Governance Must Act Before Output Acquires Force&rdquo;</span>{' '}
                (July 2026). Kojiru is the on-chain implementation of the architecture that piece describes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Rogue modes → Kojiru countermeasure */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-rogue-modes-section">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 mb-5" data-testid="compliance-rogue-modes-badge">
              <AlertTriangle className="w-3.5 h-3.5" />
              Rogue-agent failure modes
            </div>
            <h2 className="text-3xl font-bold mb-3" data-testid="compliance-rogue-modes-heading">
              Five ways an agent goes rogue &mdash; and where Kojiru is on each countermeasure today.
            </h2>
            <p className="text-gray-400 mb-10 max-w-3xl">
              Enterprise security teams already have a shared vocabulary for how agents fail. We map
              every failure mode to a specific countermeasure primitive &mdash; and we say honestly whether
              that primitive is live on Base mainnet today or an off-chain Kojiru operation with an
              on-chain migration on the roadmap.
            </p>

            <div className="grid gap-4" data-testid="compliance-rogue-modes-grid">
              {rogueModes.map((row) => {
                const Icon = row.icon;
                const isMainnet = row.tense === 'mainnet today';
                const pillColor = isMainnet
                  ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                  : 'bg-amber-500/10 border-amber-500/25 text-amber-300';
                return (
                  <div
                    key={row.id}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] hover:border-emerald-500/25 hover:bg-white/[0.03] transition p-6 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-start"
                    data-testid={`compliance-rogue-mode-${row.id}`}
                  >
                    <div className="md:col-span-4 flex items-start gap-3">
                      <div className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <Icon className="w-5 h-5 text-amber-300" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-amber-300/80 font-semibold mb-1">Failure mode</div>
                        <div className="text-base font-semibold text-white leading-snug">{row.mode}</div>
                        <div className="text-sm text-gray-400 mt-2 leading-relaxed">{row.failure}</div>
                      </div>
                    </div>

                    <div className="hidden md:flex md:col-span-1 justify-center pt-1 text-gray-600">
                      <ArrowRight className="w-5 h-5" />
                    </div>

                    <div className="md:col-span-7">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-semibold flex items-center gap-1.5">
                          <Zap className="w-3 h-3" /> Kojiru countermeasure
                        </div>
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${pillColor}`} data-testid={`compliance-rogue-tense-${row.id}`}>
                          {row.tense}
                        </span>
                      </div>
                      <div className="text-base font-semibold text-white leading-snug mb-2">
                        {row.countermeasure}
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">{row.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-8 text-sm text-gray-500 max-w-3xl">
              We separate what is enforced by a contract on Base mainnet today from what runs in
              Kojiru&rsquo;s backend. Buyers can verify every mainnet claim on BaseScan without our
              cooperation. Everything else is described in the tense it earns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Personas */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.01]" data-testid="compliance-personas-section">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold mb-3" data-testid="compliance-personas-heading">
              How your team already uses this
            </h2>
            <p className="text-gray-400 mb-10 max-w-2xl">
              Same primitives, translated into the vocabulary each function already lives in.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {personas.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.id}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-emerald-500/30 transition"
                    data-testid={`compliance-persona-${p.id}`}
                  >
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
                      <Icon className="w-5 h-5 text-emerald-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">{p.body}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Recourse Layer(TM) library */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-recourse-layer-section">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/25 text-xs text-red-300 mb-5" data-testid="compliance-recourse-layer-badge">
              <Award className="w-3.5 h-3.5" />
              The category we named &mdash; USPTO application filed 7-12-2026
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight" data-testid="compliance-recourse-layer-heading">
              The Recourse Layer<sup className="text-lg text-red-300">&trade;</sup>
            </h2>
            <p className="text-gray-400 mb-2 max-w-3xl leading-relaxed">
              Not the layer that decides if an agent&rsquo;s action is <em>right</em>. The layer that proves, at
              the moment of execution, three things every regulator eventually asks &mdash; <em>what state the
              delegation was in, what identity acted, what boundary held.</em> Every Recourse Layer&trade; asset
              Kojiru has published lives here, in one place.
            </p>
            <p className="text-sm text-red-300/80 mb-10 max-w-3xl">
              &ldquo;The Recourse Layer&rdquo; is a claimed trademark of Kojiru. USPTO application filed 7-12-2026.
              All factual claims in these assets trace to <code className="text-red-200 bg-red-500/10 px-1 py-0.5 rounded text-xs">MARKETING_FACTS.md</code>.
            </p>

            <div className="grid gap-4" data-testid="compliance-recourse-layer-grid">
              {recourseLayerAssets.map((asset) => {
                const Icon = asset.icon;
                return (
                  <div
                    key={asset.id}
                    className="rounded-2xl border border-white/10 bg-gradient-to-br from-red-500/[0.04] to-white/[0.01] hover:border-red-500/30 hover:from-red-500/[0.06] transition p-6 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-start"
                    data-testid={`compliance-recourse-asset-${asset.id}`}
                  >
                    <div className="md:col-span-4 flex items-start gap-3">
                      <div className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-red-500/10 border border-red-500/25">
                        <Icon className="w-5 h-5 text-red-300" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-red-300/80 font-semibold mb-1">
                          {asset.kind}
                        </div>
                        <div className="text-base font-semibold text-white leading-snug">{asset.title}</div>
                      </div>
                    </div>

                    <div className="md:col-span-8">
                      <p className="text-sm text-gray-400 leading-relaxed mb-4">{asset.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {asset.links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.href}
                            target={link.href.startsWith('http') || link.href.endsWith('.pdf') || link.href.endsWith('.md') || link.href.endsWith('.webm') || link.href.endsWith('.html') ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition ${
                              link.primary
                                ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:brightness-110 text-white'
                                : 'border border-white/15 hover:bg-white/5 text-gray-300'
                            }`}
                            data-testid={`compliance-recourse-link-${asset.id}-${idx}`}
                          >
                            {link.primary ? <Download className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-8 text-sm text-gray-500 max-w-3xl">
              Publishing sequence for the two feature articles is deliberate: category framing first
              (&ldquo;Solution Won&rsquo;t Arrive as a Product&rdquo;), category naming second (&ldquo;It&rsquo;s
              Called the Recourse Layer&rdquo;), integration reference third. Read in that order for a
              regulator or board audience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Live proof */}
      <section className="py-16 px-6 border-t border-white/5" data-testid="compliance-liveproof-section">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 mb-6">
              <Eye className="w-3.5 h-3.5" /> Live &mdash; no vendor cooperation required
            </div>
            <h2 className="text-3xl font-bold mb-4" data-testid="compliance-liveproof-heading">
              Every mainnet claim on this page is verifiable on BaseScan, right now.
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Our public dashboard reads the same on-chain contracts and honest-status feed you can query
              directly. If any pill flips, this page tells you the same thing our team sees.
            </p>
            <a
              href="https://credit.kojiru.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center h-13 px-8 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 font-semibold text-base transition"
              data-testid="compliance-liveproof-open-dashboard"
            >
              Open credit.kojiru.com <ExternalLink className="ml-2 w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 px-6 border-t border-white/5" data-testid="compliance-cta-section">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.06] to-teal-500/[0.02] p-10 sm:p-14">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="compliance-cta-heading">
                Bring your risk questions. We&rsquo;ll bring the receipts.
              </h2>
              <p className="text-gray-400 max-w-2xl mb-8 leading-relaxed">
                We host a 30-minute compliance briefing with prospective institutional integrators. No sales pitch.
                Your questions, our on-chain proofs, and an honest map of what&rsquo;s shipped versus what&rsquo;s on the
                roadmap. Bring your GRC checklist &mdash; we&rsquo;ll go line by line.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center h-12 px-6 rounded-xl bg-white text-black hover:brightness-95 font-semibold text-base transition"
                  data-testid="compliance-cta-book"
                >
                  <Calendar className="mr-2 w-4 h-4" /> Book a briefing
                </Link>
                <a
                  href="/downloads/Kojiru_Compliance_Primer.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center h-12 px-6 rounded-xl border border-white/15 hover:bg-white/5 font-semibold text-base transition"
                  data-testid="compliance-cta-primer"
                >
                  <FileText className="mr-2 w-4 h-4" /> Read the primer
                </a>
                <Link
                  to="/litepaper"
                  className="inline-flex items-center h-12 px-6 rounded-xl border border-white/15 hover:bg-white/5 font-semibold text-base transition"
                  data-testid="compliance-cta-litepaper"
                >
                  <Shield className="mr-2 w-4 h-4" /> Full litepaper
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
